import { useCallback, useState } from 'react';
import { fetchSubscriptionPlans } from 'services/api/subscriptions';
import { SubscriptionListRow } from 'types/api/subscriptions';

import useOrgEffect from 'hooks/useOrgEffect';

interface UseSubscriptionPlansResult {
  results: SubscriptionListRow[];
  total: number;
  loading: boolean;
  refetch: () => void;
}

/**
 * REST-backed plans hook (P18 closure). Lives under src/hooks/ since
 * P29 deletes the gql tree.
 */
const useSubscriptionPlans = (): UseSubscriptionPlansResult => {
  const [results, setResults] = useState<SubscriptionListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchSubscriptionPlans(0, 999);
        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch {
        // swallow — empty list on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { results, total, loading, refetch };
};

export default useSubscriptionPlans;
